import React, { useEffect, useRef, useState } from "react";
import { ProColumns } from "@ant-design/pro-table/es/typing";
import clsx from "clsx";
import dayjs from "dayjs";
import { Badge, Button, Select, Tag } from "antd";
import { HomeOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { useNavigate, useSearchParams } from "react-router";
import Table from "../../Common/Table";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { useBreadcrumbs } from "../../../context/breadcrumbs";
import { ActionType, RequestData } from "@ant-design/pro-components";
import { SortOrder } from "antd/es/table/interface";
import { useUsersTableGet, UserTableRow } from "../../../hooks/api/users";
import { queryFilterParams, getSorterParams } from "../../../utils";
import {
  UserPlanDisplay,
  UserRoleDisplay,
  UserStatusDisplay,
} from "../../../enums/user";
import { UserStatusType } from "../../../store/auth";
import { dateTimeFormat } from "../../../contstant";

export enum UserStatusColor {
  "active" = "green",
  "pending" = "orange",
  "inactive" = "gray",
  "banned" = "red",
}

export function getUserStatusColor(status: UserStatusType): string {
  // @ts-ignore take a color from the list
  return UserStatusColor[status?.toLowerCase()] || "rgba(0,0,0,.15)";
}
const columns = (t: TFunction): ProColumns<UserTableRow>[] => [
  {
    title: "Name",
    dataIndex: "firstName",
    key: "firstName",
    sorter: true,
    hideInSearch: true,
    renderText: (_, record) => (
      <span>
        {record.firstName} {record.lastName}
      </span>
    ),
  },
  {
    title: t("email"),
    dataIndex: "email",
    key: "email",
    hideInSearch: true,
    sorter: true,
  },
  {
    title: t("pages.users.role"),
    dataIndex: "role",
    key: "role",
    sorter: true,
    hideInSearch: true,
    valueEnum: UserRoleDisplay,
  },
  {
    title: t("common.table.createdAt"),
    dataIndex: "createdAt",
    key: "createdAt",
    hideInSearch: true,
    sorter: true,
    renderText: (text) => dayjs(text).format(dateTimeFormat),
  },
  {
    title: "Last log in",
    dataIndex: "lastLoginAt",
    key: "lastLoginAt",
    hideInSearch: true,
    sorter: true,
    renderText: (text) => {
      if (!text || text === "null" || text === "undefined") return "-";
      const date = dayjs(text);
      if (!date.isValid()) return "-";

      return date.format(dateTimeFormat);
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    sorter: true,
    hideInSearch: true,
    valueEnum: UserStatusDisplay,
    render: (_, record) => (
      <Badge
        color={getUserStatusColor(record.status)}
        text={<span className="capitalize-first-letter">{record.status}</span>}
      />
    ),
  },
];

function ToolBar() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return [
    <Button
      key="add"
      icon={<PlusCircleOutlined />}
      type="primary"
      onClick={(e) => {
        e.preventDefault();
        navigate("create");
      }}
    >
      {t("common.table.add")}
    </Button>,
  ];
}

export default function Users(): React.ReactNode | null {
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const [tableKey, setTableKey] = useState<number>(() => Date.now());
  const { t, i18n } = useTranslation();
  const { setBreadcrumbs } = useBreadcrumbs();
  const usersGet = useUsersTableGet();
  const actionRef = useRef<ActionType>(null);

  useEffect(() => {
    setBreadcrumbs([
      { title: <HomeOutlined />, href: "/" },
      { title: "Managers" },
    ]);
  }, [i18n.language, t]);

  const tableRequest = (
    {
      current,
      pageSize,
      ...args
    }: Record<string, string> & {
      pageSize?: number | undefined;
      current?: number | undefined;
      keyword?: string | undefined;
    },
    sorter: Record<string, SortOrder>,
  ): Promise<Partial<RequestData<UserTableRow>>> => {
    const newParams = queryFilterParams({
      page: current ? `${current}` : "1",
      pageSize: pageSize ? `${pageSize}` : "10",
      orderByColumn: "createdAt",
      orderBy: "DESC",
      ...args,
      ...getSorterParams(sorter),
    });

    setSearchParams({ ...args, ...getSorterParams(sorter) }, { replace: true });

    return usersGet
      .fetch({
        ...newParams,
        role: undefined,
        roles: newParams.role ? newParams?.role?.split(",") : undefined,
      })
      .then((response) => {
        if (response) {
          const { data, total } = response;

          return {
            data: (data || []).map((item) => ({
              ...item,
              key: item.id,
            })),
            success: true,
            total,
          };
        }

        return { data: [], success: false, total: 0 };
      });
  };

  return (
    <Table<UserTableRow>
      key={`${tableKey}-${i18n.language}`}
      resetKey={() => setTableKey(Date.now())}
      className={clsx("transparent")}
      headerTitle="List of Managers"
      toolBarRender={ToolBar}
      request={tableRequest}
      columns={columns(t)}
      actionRef={actionRef}
      actions={[
        [
          "edit",
          ({ record }) => ({
            title: "Edit user",
            onClick: (e: MouseEvent) => {
              e.preventDefault();
              const currentPage = actionRef.current?.pageInfo?.current;
              navigate(`${record.id}/edit`, {
                state: { fromPage: currentPage?.toString() },
              });
            },
          }),
        ],
      ]}
      search={{
        style: {
          display: "none",
        },
      }}
      options={{
        search: {
          name: "search",
          placeholder: "Search",
          style: { width: 296 },
        },
      }}
      columnsState={{
        persistenceKey: "pro-table-users",
        persistenceType: "localStorage",
      }}
    />
  );
}
